/**
 * MarketingTemplatesPage — Phase 8 Prompt 9 implementation.
 *
 * Per audit 05D_TEMPLATE_MANAGER.md (marketing mode — full CRUD).
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import PillarPageShell from "../shared/PillarPageShell";
import {
  ActionButton,
  StarterChooser,
  type StarterEntry,
} from "../../ops";
import { EmptyState } from "../../help";
import apiService from "../../../services/api";
import MarketingTemplateEditorDialog from "./MarketingTemplateEditorDialog";
import {
  formFromApiRow,
  isMarketingNurtureTemplate,
  MARKETING_TEMPLATE_TABS,
  parseEmailTemplatesListResponse,
  templateMatchesCategory,
} from "./marketingTemplateUtils";

const MARKETING_TEMPLATES_BASE =
  "/dashboard/communications/marketing/templates";

interface TemplateRow {
  id: string;
  template_key: string;
  template_name: string;
  category: string;
  status: string;
  is_active: boolean;
  brevo_sync_status: string;
  times_sent: number;
  last_sent_at: string | null;
  kind: "transactional" | "nurture";
  version: number;
  /** Plan 15 §3.3 — honest copy state. A stub can no longer read as Ready. */
  copy_state?: "ready" | "stub" | "held" | "rewrite_pending";
  copy_state_reason?: string | null;
  /** Plan 15 §3.2 — every funnel that sends this template. */
  used_in?: { slug: string; name: string; status: string; step_order: number }[];
  /** Plan 15 §3.5 — the REAL send count, from email_sending_log. */
  send_count?: number;
  content_length?: number;
}

/**
 * Plan 15 §3.3 — "Replace the binary status with: Ready · Stub · Held ·
 * Rewrite pending · Never sent · Archived." Copy state and send history are
 * different axes, so both are shown; conflating them is how two templates came
 * to be described as never sent when both had sent.
 */
const COPY_STATE_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  ready:           { bg: "#DCEBE3", fg: "#146152", label: "Ready" },
  stub:            { bg: "#EDE7D9", fg: "#6B5F45", label: "Stub — no copy" },
  held:            { bg: "#F1E3E7", fg: "#7D1935", label: "Held" },
  rewrite_pending: { bg: "#FBF3DA", fg: "#9A7B0A", label: "Rewrite pending" },
};

const CopyStateChip: React.FC<{ row: TemplateRow }> = ({ row }) => {
  const state = row.copy_state || "stub";
  const st = COPY_STATE_STYLE[state] || COPY_STATE_STYLE.stub;
  const chip = (
    <Chip
      label={st.label}
      size="small"
      sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 700, fontSize: "0.65rem", height: 20 }}
    />
  );
  return row.copy_state_reason ? (
    <Tooltip title={row.copy_state_reason}>{chip}</Tooltip>
  ) : chip;
};

const MarketingTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ "*"?: string }>();
  const currentCategory = (params["*"] ?? "all").split("/")[0] || "all";

  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editorTemplateId, setEditorTemplateId] = useState<string | null>(null);
  const [editorStarter, setEditorStarter] = useState<StarterEntry | null>(null);
  const [editorInitialTab, setEditorInitialTab] = useState(0);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await apiService.get("/api/admin/email-templates?limit=200");
      const rows = parseEmailTemplatesListResponse(res) as TemplateRow[];
      const nurture = rows.filter((r: TemplateRow) =>
        isMarketingNurtureTemplate(r),
      );
      setTemplates(nurture);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredRows = useMemo(() => {
    let rows = templates;
    if (currentCategory !== "all") {
      rows = rows.filter((r) =>
        templateMatchesCategory(r.category, currentCategory),
      );
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.template_key.toLowerCase().includes(q) ||
          (r.template_name?.toLowerCase() ?? "").includes(q),
      );
    }
    return rows;
  }, [templates, currentCategory, search]);

  const openCreateFromStarter = (starter: StarterEntry) => {
    setEditorMode("create");
    setEditorTemplateId(null);
    setEditorStarter(starter);
    setEditorInitialTab(0);
    setEditorOpen(true);
  };

  const openEdit = (row: TemplateRow, tab = 0) => {
    setEditorMode("edit");
    setEditorTemplateId(row.id);
    setEditorStarter(null);
    setEditorInitialTab(tab);
    setEditorOpen(true);
  };

  const openDuplicate = async (row: TemplateRow) => {
    try {
      const res: any = await apiService.get(
        `/api/admin/email-templates/${row.id}`,
      );
      const data = formFromApiRow(res?.data ?? res);
      setEditorMode("create");
      setEditorTemplateId(null);
      setEditorStarter({
        id: `${data.template_key}_copy`,
        name: `${data.template_name} (copy)`,
        useCase: data.description,
        category: data.category,
        subjectScaffold: data.subject_line,
        previewTextScaffold: data.preview_text,
        html_content: data.html_content,
        plain_text_content: data.plain_text_content,
      });
      setEditorInitialTab(0);
      setEditorOpen(true);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load template for duplicate");
    }
  };

  const handleStarterSelect = (starter: StarterEntry) => {
    setChooserOpen(false);
    openCreateFromStarter(starter);
  };

  return (
    <PillarPageShell
      pillar="marketing"
      title="Marketing Templates"
      subtitle="Discretionary outreach templates. Full CAN-SPAM footer auto-applied at render time."
      helpTipId="template.kind.badge"
      rightActions={
        <ActionButton
          verb="duplicate"
          labelOverride="+ New Template"
          onClick={() => setChooserOpen(true)}
          showIcon={false}
          variantOverride="primary"
        />
      }
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={currentCategory}
          onChange={(_, v) =>
            navigate(
              v === "all"
                ? MARKETING_TEMPLATES_BASE
                : `${MARKETING_TEMPLATES_BASE}/${v}`,
            )
          }
          variant="scrollable"
          scrollButtons="auto"
        >
          {MARKETING_TEMPLATE_TABS.map((c) => (
            <Tab
              key={c.id}
              value={c.id}
              label={c.label}
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by template_key or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={48} />
              ))}
            </Box>
          ) : filteredRows.length === 0 ? (
            <EmptyState
              surface="templates.empty.marketing"
              onCreate={() => setChooserOpen(true)}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template Key</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell align="right">Sends</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openEdit(row)}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                      >
                        {row.template_key}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        {row.template_name}
                      </Typography>
                      {row.used_in && row.used_in.length > 0 && (
                        <Tooltip
                          title={row.used_in
                            .map((u) => `${u.name} (step ${u.step_order}, ${u.status})`)
                            .join(" · ")}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: row.used_in.length > 1 ? "#7D1935" : "text.secondary",
                                  fontWeight: row.used_in.length > 1 ? 700 : 400 }}
                          >
                            {row.used_in.length > 1
                              ? `⚠ shared by ${row.used_in.length} funnels`
                              : `used in ${row.used_in[0].name}`}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="N"
                        size="small"
                        sx={{
                          bgcolor: "#D4AC0D",
                          color: "#1B1F23",
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          height: 20,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <CopyStateChip row={row} />
                    </TableCell>
                    <TableCell>v{row.version ?? 1}</TableCell>
                    <TableCell align="right">
                      {(row.send_count ?? 0) > 0 ? (
                        <Typography variant="body2">{row.send_count}</Typography>
                      ) : (
                        <Tooltip title={
                          row.copy_state === "ready"
                            ? "This template has real copy and has never been sent to anyone."
                            : "No copy yet, so nothing has been sent."
                        }>
                          <Typography
                            variant="caption"
                            sx={{ color: row.copy_state === "ready" ? "#7D1935" : "text.secondary",
                                  fontWeight: row.copy_state === "ready" ? 700 : 400 }}
                          >
                            Never sent
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          gap: 0.5,
                          justifyContent: "flex-end",
                        }}
                      >
                        <ActionButton
                          verb="preview"
                          size="small"
                          showIcon={false}
                          onClick={() => openEdit(row, 2)}
                        />
                        <ActionButton
                          verb="edit"
                          size="small"
                          showIcon={false}
                          onClick={() => openEdit(row, 1)}
                        />
                        <ActionButton
                          verb="duplicate"
                          size="small"
                          showIcon={false}
                          onClick={() => openDuplicate(row)}
                        />
                        {/* Content lives here; the numbers live on the
                            performance page. One click between them. */}
                        <ActionButton
                          verb="view_analytics"
                          labelOverride="Performance"
                          size="small"
                          showIcon={false}
                          onClick={() =>
                            navigate(
                              `/dashboard/communications/marketing/templates/${encodeURIComponent(row.template_key)}/performance`,
                            )
                          }
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <StarterChooser
        entityType="template"
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onSelect={handleStarterSelect}
      />

      <MarketingTemplateEditorDialog
        open={editorOpen}
        mode={editorMode}
        templateId={editorTemplateId}
        starter={editorStarter}
        initialTab={editorInitialTab}
        onClose={() => {
          setEditorOpen(false);
          setEditorStarter(null);
        }}
        onSaved={loadTemplates}
      />
    </PillarPageShell>
  );
};

export default MarketingTemplatesPage;
